import { inject, injectable } from 'inversify';
import { Observable, firstValueFrom, from, throwError, timer } from 'rxjs';
import { catchError, mergeMap, retry, take, tap } from 'rxjs/operators';

import { FileUpload } from '../models/file-upload';
import { Logger } from '../models/logger';
import { GAContants } from '../types/ga.types';
import { OriginalInputFile, ProcessorMetadata } from '../types/types';
import { castArray, isString } from '../utils/lodash-min';
import { metricsOrNull } from '../utils/metric-emit';
import { ProcessorMetadataUtils } from '../utils/processor-metadata.utils';
import { GAService } from './analytics.service';
import { ApiConfig } from './api-config';
import { EnforcementErrorService } from './enforcement-error.service';
import { FileManagerService } from './file-manager.service';
import { HelperService } from './helper.service';
import { LightHttp } from './light-http.service';
import { OfflineTaskService } from './offline-task.service';
import { SettingsService } from './settings.service';
import { WebSocketService } from './websocket.service';
import { scheduleCoordinator } from './scheduling/schedule-coordinator';
import { ScheduleFailure, createScheduleRetryPolicy } from './scheduling/schedule-retry-policy';
import { scheduleInFlight } from './scheduling/schedule-in-flight';
import { isUploadExpiredError } from '../utils/upload-expiry';
import { appendRenderedRefusals, enforcementRenderedBy, markEnforcementRendered, markRefusalsRendered } from './scheduling/schedule-refusals';
import { unsupportedRefusalOf } from '../utils/unsupported-input';
import { isVideoFromType, isVideoToType } from '../utils/media/media-type-detector';
import { ScheduleWorkKind } from './scheduling/batch-schedule-gate';


export interface ProbedData {
    approxTime: number;
    approxSize: number;
}

/**
 * Full POST /files/source/probe response surface: mp4/webm branch fields plus
 * the gif estimator branch (ironman GifProbeEstimator; -1 marks unavailable).
 */
export interface SourceProbeInfo extends Partial<ProbedData> {
    /** milliseconds, matching the mp4 branch */
    originalDuration?: number;
    originalSize?: number;
    naturalGifEstimate?: number;
    floorSize?: number;
    quickOnly?: boolean;
    error?: boolean;
}

export interface SourceProbeParams {
    /** Edge that holds the uploaded file (uploadDomain of its metadata). */
    processorMetadata: ProcessorMetadata;
    /** Upload responses identifying the already-stored files on that edge. */
    originalInputFiles: OriginalInputFile[];
    id: string;
    fromType: string | string[];
    toType: string;
    processType?: string;
    options: any;
    /** Sample seconds for the mp4/webm branch (the gif branch fixes ~1s). */
    probeDuration?: number;
    /** true = quick section only (no sample encodes) — the gif branch's Stage 1. */
    estimateOnly?: boolean;
}

export interface FileProcessResponse {
    originalInputFiles: string[];
}

@injectable()
export class FileProcessService {
    private readonly logger = Logger.getLogger('FileProcessService');

    constructor(
        @inject(LightHttp) private http: LightHttp,
        @inject(WebSocketService) private wsService: WebSocketService,
        @inject(FileManagerService) private fileManagerService: FileManagerService,
        @inject(GAService) private gaService: GAService,
        @inject(OfflineTaskService) private offlineTaskService: OfflineTaskService,
        @inject(HelperService) private helperService: HelperService,
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(SettingsService) private settingsService: SettingsService,
        @inject(EnforcementErrorService) private enforcementErrorService: EnforcementErrorService,
    ) { }

    /**
     * RxJS retry helper that bails immediately on enforcement / billing
     * rejections (HTTP 429 enforcement_error, HTTP 402
     * insufficient_credits) and surfaces them via the global
     * EnforcementErrorModal. Without this, the convert / schedule path
     * would silently retry 429s with no UI surface (runbook bug 6a-B1).
     */
    private retryConfigWithEnforcementBailout(count: number, delayMs: number, fileUploads?: FileUpload) {
        return {
            count,
            delay: (error: unknown) => {
                if (EnforcementErrorService.isEnforcementError(error)) {
                    if (!enforcementRenderedBy(fileUploads)) {
                        this.enforcementErrorService.show(
                            EnforcementErrorService.extractDetails(error),
                        );
                    }
                    return throwError(() => error);
                }
                // FIX-2 (C2): 410 UPLOAD_EXPIRED is the server refusing before any workflow exists; re-sending the same
                // inputs gets the same answer, so it is never retried. Tagged for the caller's typed handling.
                if (isUploadExpiredError(error)) {
                    (error as { uploadExpired?: boolean }).uploadExpired = true;
                    return throwError(() => error);
                }
                // FIX-6: a 422 UNSUPPORTED_INPUT is the same answer every time (the file itself can't be
                // converted); re-sending it would only delay the row's "Can't convert".
                if (unsupportedRefusalOf(error)) return throwError(() => error);
                return timer(delayMs);
            },
        };
    }

    /**
     * @param opts.rendersRefusals the typed schedule refusals the CALLER renders (INT1-P2-02,
     *   `scheduling/schedule-refusals.ts`). professorx refuses only codes listed here; omitted = today's path.
     */
    processFile(fileUploads: FileUpload, opts?: { rendersRefusals?: readonly string[], /** gif-maker (wave 2 ERRORS): the caller renders 429 / 402 refusals itself — no global modal. Default: unchanged. */ rendersEnforcement?: boolean }): Observable<FileProcessResponse[] | any> {
        markRefusalsRendered(fileUploads, opts?.rendersRefusals);
        if (opts?.rendersEnforcement) markEnforcementRendered(fileUploads);
        return from(this.wsService.ensureWsDomain()).pipe(
            mergeMap(() => this._processFileInternal(fileUploads)),
        );
    }

    private _processFileInternal(fileUploads: FileUpload): Observable<FileProcessResponse[] | any> {
        this.gaService.send(
            GAContants.HitType.EVENT,
            GAContants.EventCategory.BUTTON,
            GAContants.EventAction['FILE-CONVERT-START'],
            GAContants.EventLabel.CONVERT,
            '',
        );
        // Anchor for CONVERT_COMPLETE/_ERROR `ms`. CONVERT_START is emitted once
        // per Convert click, which can cover N files, so the per-workflow
        // duration is measured from the point THIS upload is dispatched.
        metricsOrNull()?.markConvertStart(fileUploads.id);
        this.fileManagerService.saveFileUploadToStorage(fileUploads);
        this.logger.trace7({ message: 'FileUpload sent for processing', data: FileUpload.toLoggableObject(fileUploads) });
        const processorMetadata = fileUploads.endpointData.processorMetadataList[0];

        return from(firstValueFrom(this.settingsService.featureFlags.pipe(take(1)))).pipe(
            mergeMap((ff) => {
                const hasUnuploadedFiles = fileUploads.file && !FileUpload.isUploadedFile(fileUploads);
                if (ff?.useTaskScheduling && !hasUnuploadedFiles) {
                    // `clientQueuedState` (MONGO-STALL-PLAN-v5.2.md §4, rollout
                    // step 5). Read here because this is the one place the flag
                    // emission is already in scope; published on the coordinator
                    // so the card and the batch header — which have no
                    // SettingsService edge — can read it without adding one.
                    // OFF is today's path, byte for byte.
                    scheduleCoordinator.enabled = ff?.clientQueuedState === true;
                    if (scheduleCoordinator.enabled) {
                        return this._scheduleTaskHandsOff(fileUploads, processorMetadata);
                    }
                    return this._scheduleTask(fileUploads, processorMetadata);
                }
                const updateProcessDomain$ = fileUploads.updateProcessDomain(
                    this.http, this.fileManagerService,
                    this.apiConfig.getUploadAndProcessEndpointResolveEndpoint(), this.wsService,
                );
                return updateProcessDomain$.pipe(
                    mergeMap(() => this._processFile(fileUploads, processorMetadata)),
                );
            }),
        );
    }

    private _scheduleTask(fileUploads: FileUpload, processorMetadata: ProcessorMetadata) {
        const url = this.apiConfig.getScheduleTaskEndpoint();
        const formData = this._buildFormData(fileUploads);
        return this.http.request<any>(url, 'POST', formData,
            { headers: this.helperService.getCommonHttpHeaders(), timeout: 30000 })
            .response$.pipe(
                retry(this.retryConfigWithEnforcementBailout(2, 1000, fileUploads)),
                tap((response) => {
                    if (response.processDomain) processorMetadata.processDomain = response.processDomain;
                    if (response.downloadDomain) processorMetadata.downloadDomain = response.downloadDomain;
                    // B3: same as `_onScheduleAccepted` — the flag-off path
                    // schedules too, and its card needs the id for the same reasons.
                    if (response.workflowId) fileUploads.workflowId = String(response.workflowId);
                    fileUploads.persistFileUpload(this.fileManagerService);
                    this.logger.trace7({ message: 'Task scheduled', data: { workflowId: response.workflowId } });
                    this.gaService.send(GAContants.HitType.EVENT, GAContants.EventCategory.BUTTON,
                        GAContants.EventAction['FILE-CONVERT-END'], GAContants.EventLabel.CONVERT, '');
                }),
                catchError((err) => {
                    if (err?.uploadExpired) {
                        // FIX-2: an expected refusal with its own UI, not an error worth paging on.
                        this.logger.warn({ message: 'Schedule refused: upload expired', error: err });
                    } else if (unsupportedRefusalOf(err)) {
                        // FIX-6 (fu-t8): 422 UNSUPPORTED_INPUT is the same kind of answer -- the row says "Can't convert".
                        this.logger.warn({ message: 'Schedule refused: unsupported input', error: err });
                    } else {
                        this.logger.error({ message: `Error scheduling task: ${err.message}`, error: err });
                    }
                    this.gaService.send(GAContants.HitType.EVENT, GAContants.EventCategory.BUTTON,
                        GAContants.EventAction['FILE-CONVERT-FAILED'], GAContants.EventLabel.CONVERT, '');
                    // Tag enforcement errors so the component can show retry UI instead of removing the file
                    if (err.response?.dataType === 'enforcement_error') {
                        err.isEnforcementError = true;
                        err.enforcementData = err.response;
                    }
                    return throwError(() => err);
                }),
            );
    }

    /**
     * Hands-off scheduling — the `clientQueuedState` path (2026-09-05 Mongo
     * write-stall incident; MONGO-STALL-PLAN-v5.2.md §4, corrections 16-22).
     *
     * WHAT CHANGES versus `_scheduleTask` above:
     *   - the fan-out is capped at 4 concurrent schedule calls and a
     *     `Retry-After` on ANY call pauses the whole batch (`BatchScheduleGate`);
     *   - failures follow the jittered 2/5/10/20/30 s ladder inside a 5-minute
     *     budget instead of `retry(2, 1000)` (~92 s of wall clock, correction 17);
     *   - a 409 `schedule_in_flight` is retried at the SERVER's pace instead of
     *     being blind-retried twice against a claim that is still running;
     *   - every call carries `Idempotency-Key: <id>:<nonce>`, so a retry replays
     *     the first outcome instead of creating a second workflow;
     *   - NOTHING here removes the card or deletes the uploaded bytes. The card
     *     goes queued -> retrying -> stalled and the user keeps their file.
     *
     * The returned observable emits the FIRST outcome and completes: the
     * schedule response, or a `{ queued: true }` sentinel once the card has
     * become the coordinator's business. The retry ladder then runs on in the
     * background — the whole point of "hands-off" is that the user is not made
     * to sit and watch a spinner while it does (plan §4.4).
     */
    private _scheduleTaskHandsOff(fileUploads: FileUpload, processorMetadata: ProcessorMetadata) {
        this.scheduleTargets.set(fileUploads.id, { fileUpload: fileUploads, processorMetadata });
        scheduleCoordinator.registerDispatcher(id => this._redispatchSchedule(id));
        scheduleCoordinator.begin(fileUploads.id);
        return from(this._runScheduleLadder(fileUploads, processorMetadata));
    }

    /**
     * Cards this service has dispatched, so the batch-level "Retry all" can
     * re-send them. Entries are dropped on a terminal outcome, so the map
     * cannot outgrow the number of cards on the page.
     */
    private readonly scheduleTargets = new Map<string, { fileUpload: FileUpload, processorMetadata: ProcessorMetadata }>();

    /**
     * Cards whose ladder the user stopped (Studio round 6, QA#4: "Cancel" / "Retry now" on the
     * render sheet). Checked after every wait in `_scheduleLadderLoop`, so a cancelled card is never
     * POSTed again; the attempt already in flight, if any, is left to finish -- professorx's
     * idempotency key makes a late success a replay, not a second workflow. Nothing here removes
     * the card or the uploaded bytes.
     */
    private readonly cancelledSchedules = new Set<string>();

    /** Stop retrying this card. Additive; the flag-off path never reaches the ladder. */
    cancelSchedule(fileUploadId: string): void {
        if (!fileUploadId) return;
        this.cancelledSchedules.add(fileUploadId);
        this.scheduleTargets.delete(fileUploadId);
        scheduleCoordinator.forget(fileUploadId);
    }

    /** "Retry all" for one card: a NEW nonce, then the ladder from attempt 1. */
    private _redispatchSchedule(fileUploadId: string) {
        const target = this.scheduleTargets.get(fileUploadId);
        if (!target) return;
        scheduleCoordinator.renewNonce(fileUploadId);
        target.fileUpload.scheduleAttemptNonce = scheduleCoordinator.nonceFor(fileUploadId);
        this.fileManagerService.saveFileUploadToStorage(target.fileUpload);
        this._runScheduleLadder(target.fileUpload, target.processorMetadata)
            .catch(err => this.logger.error({ message: `Retry-all schedule failed: ${err?.message}`, error: err }));
    }

    /**
     * Resolve/reject on the FIRST outcome while the ladder keeps running.
     * `startConversion` awaits this, so the user is moved to their downloads as
     * soon as the card has a state worth showing, not 5 minutes later.
     */
    private _runScheduleLadder(fileUploads: FileUpload, processorMetadata: ProcessorMetadata): Promise<any> {
        let settled = false;
        let resolveFirst: (value: any) => void;
        let rejectFirst: (reason: any) => void;
        const first = new Promise<any>((resolve, reject) => { resolveFirst = resolve; rejectFirst = reject; });
        const resolveOnce = (value: any) => { if (!settled) { settled = true; resolveFirst(value); } };
        const rejectOnce = (reason: any) => { if (!settled) { settled = true; rejectFirst(reason); } };
        // WEB-CORE-06: the card knows its schedule is on the ladder (flag-independent), until the ladder ends however it ends.
        scheduleInFlight.add(fileUploads?.id);
        void this._scheduleLadderLoop(fileUploads, processorMetadata, resolveOnce, rejectOnce)
            .finally(() => scheduleInFlight.delete(fileUploads?.id));
        return first;
    }

    private async _scheduleLadderLoop(
        fileUploads: FileUpload,
        processorMetadata: ProcessorMetadata,
        resolveOnce: (value: any) => void,
        rejectOnce: (reason: any) => void,
    ): Promise<void> {
        const policy = createScheduleRetryPolicy({
            startedAtMs: scheduleCoordinator.startedAtMs(fileUploads.id),
        });
        let attempt = 0;
        for (; ;) {
            attempt++;
            try {
                // The gate, not `Promise.all`, decides when this call goes out:
                // 4 slots shared by the whole batch, and a shared pause while a
                // Retry-After is outstanding (plan §4.2, correction 11).
                const response: any = await scheduleCoordinator.gate.run(
                    () => firstValueFrom(this._scheduleOnce(fileUploads, processorMetadata)),
                    FileProcessService.scheduleWorkKindOf(fileUploads),
                );
                this._onScheduleAccepted(fileUploads, processorMetadata, response, attempt);
                resolveOnce(response);
                return;
            } catch (err: any) {
                // FIX-2 (C2): the upload is gone -- a deterministic refusal with its own UI (no dialog, the card and the
                // bytes the user still has are kept). Not a ladder decision: tag it and hand it to the caller once.
                if (isUploadExpiredError(err)) {
                    err.uploadExpired = true;
                    this._endScheduleAttempt(fileUploads.id, 'upload_expired');
                    rejectOnce(err);
                    return;
                }
                const failure = FileProcessService.toScheduleFailure(err);
                const decision = policy.decide(failure, attempt);
                // FIX-6 (fu-t8): a 422 UNSUPPORTED_INPUT is an expected, rendered answer (still 'terminal' below).
                (unsupportedRefusalOf(err) ? this.logger.warn : this.logger.error).call(this.logger, {
                    message: `Schedule attempt ${attempt} failed (${decision.kind}): ${err?.message}`,
                    error: err,
                });

                if (decision.kind === 'bail-enforcement') {
                    // 429 enforcement_error OR 402 insufficient_credits. Correction
                    // 20: the 402 body carries `code`, not `dataType`, so the old
                    // catchError never tagged it and the component removed the card
                    // (bytes and all) while stacking a generic dialog on top of the
                    // enforcement modal. Both envelopes are tagged here.
                    this._tagEnforcementError(err, fileUploads);
                    this._endScheduleAttempt(fileUploads.id, 'enforcement');
                    rejectOnce(err);
                    return;
                }

                if (decision.kind === 'terminal') {
                    // A deterministic 4xx: retrying re-sends the same body for the
                    // same answer. This is the ONLY failure class that still lets
                    // the component remove the card and the uploaded bytes.
                    err.scheduleTerminal = true;
                    this._endScheduleAttempt(fileUploads.id, 'terminal');
                    rejectOnce(err);
                    return;
                }

                if (decision.kind === 'budget-exhausted') {
                    // Plan §4.4: give up at 5 min -> amber "Not started — your file
                    // is still here" plus ONE batch-level Retry all. No modal, no
                    // removeFile, no delete call to the edge domains.
                    scheduleCoordinator.onBudgetExhausted(fileUploads.id, {
                        attempt,
                        reason: decision.reason,
                    });
                    this.gaService.send(GAContants.HitType.EVENT, GAContants.EventCategory.BUTTON,
                        GAContants.EventAction['FILE-CONVERT-FAILED'], GAContants.EventLabel.CONVERT, '');
                    resolveOnce({ queued: true, stalled: true, fileUploadId: fileUploads.id });
                    return;
                }

                // 'retry': 503/5xx/network/timeout, or a 409 at the server's pace.
                const retryAfterMs = decision.fromRetryAfter ? (decision.delayMs ?? null) : null;
                scheduleCoordinator.onServerBusy(fileUploads.id, {
                    status: failure.status,
                    retryAfterMs,
                    attempt,
                });
                scheduleCoordinator.onRetryScheduled(fileUploads.id, {
                    attempt: attempt + 1,
                    delayMs: decision.delayMs ?? 0,
                    fromRetryAfter: decision.fromRetryAfter,
                    reason: decision.reason,
                    status: failure.status,
                });
                // The card now has a state worth rendering; release the caller so
                // the batch can move to /downloads and watch it there.
                resolveOnce({ queued: true, fileUploadId: fileUploads.id });
                await FileProcessService.wait(decision.delayMs ?? 0);
                if (this.cancelledSchedules.has(fileUploads.id)) {
                    this.cancelledSchedules.delete(fileUploads.id);
                    this.logger.trace7({ message: 'Schedule ladder cancelled by the user', data: { fileUploadId: fileUploads.id, attempt } });
                    return;
                }
            }
        }
    }

    /** One schedule POST — no rxjs retry, the ladder above owns that. */
    private _scheduleOnce(fileUploads: FileUpload, _processorMetadata: ProcessorMetadata): Observable<any> {
        const url = this.apiConfig.getScheduleTaskEndpoint();
        const formData = this._buildFormData(fileUploads);
        // The nonce is read back off the card first, so a tab reloaded mid-ladder
        // resumes with the SAME key and professorx replays the first outcome
        // instead of creating a duplicate workflow (correction 18).
        const nonce = scheduleCoordinator.nonceFor(fileUploads.id, fileUploads.scheduleAttemptNonce);
        if (fileUploads.scheduleAttemptNonce !== nonce) {
            fileUploads.scheduleAttemptNonce = nonce;
            this.fileManagerService.saveFileUploadToStorage(fileUploads);
        }
        // PRE-FLIP CHECK (same trap as `requestIdHeader`, METRICS.md §7.2):
        // `Idempotency-Key` is NOT a CORS-safelisted request header, so every
        // cross-origin schedule call carrying it is preflighted. professorx /
        // its nginx must list `Idempotency-Key` in `Access-Control-Allow-Headers`
        // (and `Retry-After` in `Access-Control-Expose-Headers`, or the client
        // falls back to the body's `retryAfterSeconds`) BEFORE
        // `clientQueuedState` is turned on for anyone — verify with an OPTIONS
        // probe on QA first. Noted 2026-09-05.
        const headers = {
            ...this.helperService.getCommonHttpHeaders(),
            'Idempotency-Key': `${fileUploads.id}:${nonce}`,
        };
        return this.http.request<any>(url, 'POST', formData, { headers, timeout: 30000 }).response$;
    }

    /** The schedule call was accepted (fresh, or an idempotent replay). */
    private _onScheduleAccepted(
        fileUploads: FileUpload,
        processorMetadata: ProcessorMetadata,
        response: any,
        attempt: number,
    ) {
        if (response?.processDomain) processorMetadata.processDomain = response.processDomain;
        if (response?.downloadDomain) processorMetadata.downloadDomain = response.downloadDomain;
        // B3 (FRAME_PICKER_DESIGN.md F9): keep the workflow id on the card, set
        // BEFORE persist so it survives the localStorage round trip. Read by the
        // FILE_DOWNLOAD emit and sent as `workflowUuid` on the zip request.
        if (response?.workflowId) fileUploads.workflowId = String(response.workflowId);
        fileUploads.persistFileUpload(this.fileManagerService);
        // A 200 on attempt > 1 IS the dedup replay: per the M4 contract a retry
        // of a completed attempt comes back with the SAME workflowId rather than
        // creating a second one. That is plan §7 metric 5 (2.5% of upload uuids
        // had duplicate workflows during the incident; target < 0.1%).
        const dedup = attempt > 1 || response?.idempotentReplay === true;
        scheduleCoordinator.onScheduled(fileUploads.id, {
            expectedStartSeconds: response?.expectedStartBySeconds,
            attempt,
            dedup,
        });
        this.logger.trace7({ message: 'Task scheduled', data: { workflowId: response?.workflowId, attempt, dedup } });
        this.gaService.send(GAContants.HitType.EVENT, GAContants.EventCategory.BUTTON,
            GAContants.EventAction['FILE-CONVERT-END'], GAContants.EventLabel.CONVERT, '');
    }

    /** Terminal outcome: stop tracking, and let the card own its own error UI. */
    private _endScheduleAttempt(fileUploadId: string, reason: string) {
        this.scheduleTargets.delete(fileUploadId);
        scheduleCoordinator.forget(fileUploadId);
        this.gaService.send(GAContants.HitType.EVENT, GAContants.EventCategory.BUTTON,
            GAContants.EventAction['FILE-CONVERT-FAILED'], GAContants.EventLabel.CONVERT, '');
        this.logger.trace7({ message: `Schedule ended: ${reason}`, data: { fileUploadId } });
    }

    /**
     * Tag BOTH enforcement envelopes so the component keeps the card:
     * 429 `{dataType:'enforcement_error'}` and 402 `{code:'insufficient_credits'}`
     * (correction 20). The modal itself is opened here, once.
     */
    private _tagEnforcementError(err: any, fileUploads?: FileUpload) {
        if (!err) return;
        err.isEnforcementError = true;
        err.enforcementData = err.response;
        if (!enforcementRenderedBy(fileUploads)) this.enforcementErrorService.show(EnforcementErrorService.extractDetails(err));
    }

    /**
     * Code 10 (a): which gate lane a card takes. "Video work" is what the policy calls heavy on the
     * video side — a video input OR a video output (video→gif is video work; gif→gif is not paced by
     * tier here and keeps today's gate). Anything unreadable is 'other' = today's behaviour.
     */
    static scheduleWorkKindOf(fileUploads: { fromType?: unknown; toType?: unknown } | null | undefined): ScheduleWorkKind {
        try {
            const toType = typeof fileUploads?.toType === 'string' ? fileUploads.toType : '';
            const fromTypes = castArray(fileUploads?.fromType ?? []).filter((t): t is string => typeof t === 'string');
            if ((toType && isVideoToType(toType)) || (fromTypes.length > 0 && isVideoFromType(fromTypes))) return 'video';
        } catch { /* fall through */ }
        return 'other';
    }

    /** The structural view of a failure the pure retry policy consumes. */
    static toScheduleFailure(err: any): ScheduleFailure {
        return {
            status: err?.status,
            name: err?.name,
            response: err?.response,
            // `retryAfterSeconds` is already parsed and junk-rejected by the HTTP
            // layer (utils/retry-after.ts); the policy re-parses it as a number.
            retryAfter: typeof err?.retryAfterSeconds === 'number' ? err.retryAfterSeconds : null,
        };
    }

    private static wait(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, Math.max(0, ms)));
    }

    /**
     * What goes on the wire as `metadataList` -- Studio round 6 (SDE#4): ONE entry for a composition.
     *
     * The full list stays on the card (the /downloads row, re-edit and "Retry all" read it, and
     * `fileUploadedServerEndpointList` above is built from it: ironman indexes THAT per input,
     * `ConvertService.kt:374`). `metadataList` itself is parsed by ironman into
     * `filenameToMetadataMap` (`ConvertArgumentProvider.kt:153-197`, matched by name+size) -- a map
     * with NO reader anywhere in ironman or the shared library (`ConvertArguments.kt:72` is its only
     * mention), and the public API already schedules with `metadataList = null`
     * (professorx `PublicJobController.kt:1002`). professorx only forwards the string into the
     * workflow's `processArguments` (`JobSchedulingService.kt:1395`). For a 30-clip project the list
     * was 34 KB of a 58 KB body -- the same twelve edge URLs thirty times -- and 34 KB per workflow
     * document in Mongo. Non-Studio cards are untouched.
     */
    static metadataListForWire(fileUploads: FileUpload): unknown[] {
        const list = fileUploads.metadataList || [];
        const composition = (fileUploads.options as any)?.composition;
        if (composition && typeof composition === 'object' && list.length > 1) return [list[0]];
        return list;
    }

    private _buildFormData(fileUploads: FileUpload): FormData {
        const formData = new FormData();
        if (fileUploads.originalInputFiles) {
            formData.append('originalInputFiles', JSON.stringify(fileUploads.originalInputFiles));
        }
        formData.append('id', fileUploads.id);
        formData.append('processType', fileUploads.processType);
        formData.append('fromType', fileUploads.fromType.toString());
        formData.append('toType', fileUploads.toType);
        formData.append('wsId', this.wsService.getId());
        formData.append('wsDomain', this.wsService.getDomain());
        formData.append('email', fileUploads.settings.email);
        formData.append('featurePage', fileUploads.pageData?.path || '');
        // ONE PART PER VALUE. professorx binds this as a `List<String>` and its own comment
        // (TaskSchedulerController.kt:90-95) says the collection binder was chosen precisely
        // because a hostname cannot contain a comma -- so a comma-joined value would arrive as a
        // one-element list whose element is "a,b" and pin every render to a host that does not
        // exist. Appended only when there is something to say: an absent part is what every
        // non-Studio schedule sends today (STUDIO_DESIGN.md §4.5 v1, P5c.2b).
        (fileUploads.preferredEdgeDomains || []).forEach((domain) => {
            if (domain) formData.append('preferredEdgeDomains', domain);
        });
        // R14-SRV4: the processing EDGE opens this URL, not the browser — it must be absolute even
        // behind the dev proxy (`getUploadedFileDownloadEndpointForServer`).
        fileUploads.metadataList.map(m => m.processorMetadataList[0]).forEach(pmdl => {
            formData.append('fileUploadedServerEndpointList',
                ProcessorMetadataUtils.getUploadedFileDownloadEndpointForServer(pmdl));
        });
        formData.append('metadataList', JSON.stringify(FileProcessService.metadataListForWire(fileUploads)));
        formData.append('options', JSON.stringify(fileUploads.options));
        formData.append('optionsSnapshot', JSON.stringify(fileUploads.optionsSnapshot));
        // INT1-P2-02: only a submission whose caller renders the typed refusals asks for them.
        appendRenderedRefusals(formData, fileUploads);
        return formData;
    }


    private _processFile(fileUploads: FileUpload, processorMetadata: ProcessorMetadata,) {
        // console.log('HPY =========== processFile mergeMap');
        const url = ProcessorMetadataUtils.getProcessorEndpoint(processorMetadata);
        const formData: FormData = new FormData();
        if (fileUploads.file && !FileUpload.isUploadedFile(fileUploads)) {
            fileUploads.file.forEach(file => {
                formData.append('files', file, file.name); // [' + (i++) + ']
            });
        } else {
            // this is a re add event
            if (fileUploads.originalInputFiles) {
                formData.append('originalInputFiles', JSON.stringify(fileUploads.originalInputFiles));
            }
        }

        formData.append('id', fileUploads.id);
        formData.append('processType', fileUploads.processType);
        formData.append('fromType', fileUploads.fromType.toString());
        formData.append('toType', fileUploads.toType);
        formData.append('wsId', this.wsService.getId());
        formData.append('wsDomain', this.wsService.getDomain());
        formData.append('email', fileUploads.settings.email);
        formData.append('featurePage', fileUploads.pageData?.path || '');
        fileUploads.metadataList.map(m => m.processorMetadataList[0]).forEach(pmdl => {
            formData.append(
                'fileUploadedServerEndpointList',
                ProcessorMetadataUtils.getUploadedFileDownloadEndpointForServer(pmdl) // R14-SRV4: edge-addressable, see _buildFormData
            );
        });

        formData.append(
            'metadataList',
            JSON.stringify(fileUploads.metadataList),
        );
        // HPY IMP this is needed to dispatch the email. it is used in the email
        // formData.append('downloadURLPrefix', fileUploads.endpointData.domain + fileUploads.endpointData.download);
        /*
        const options = {};
        fileUploads.options.forEach(option => {
            const optionKeys = Object.keys(option);
            optionKeys.forEach(key => {
                options[key] = option[key];
            });
        });
        */
        formData.append('options', JSON.stringify(fileUploads.options));
        formData.append('optionsSnapshot', JSON.stringify(fileUploads.optionsSnapshot));

        return this.http.request<FileProcessResponse>(
            url,
            'POST',
            formData,
            { headers: this.helperService.getCommonHttpHeaders(), timeout: 30000 })
            .response$
            .pipe(
                retry(this.retryConfigWithEnforcementBailout(2, 1000, fileUploads)),
                tap(() => {
                    this.logger.trace7({ message: 'File process success: ', data: FileUpload.toLoggableObject(fileUploads) });
                    this.gaService.send(
                        GAContants.HitType.EVENT,
                        GAContants.EventCategory.BUTTON,
                        GAContants.EventAction['FILE-CONVERT-END'],
                        GAContants.EventLabel.CONVERT,
                        '',
                    );
                }),
                catchError((err) => {
                    // console.log('HPY =========== processFile catchError');
                    this.logger.error({ message: `Error processing file: ${err.message}`, error: err });
                    this.gaService.send(
                        GAContants.HitType.EVENT,
                        GAContants.EventCategory.BUTTON,
                        GAContants.EventAction['FILE-CONVERT-FAILED'],
                        GAContants.EventLabel.CONVERT,
                        '',
                    );
                    return throwError(() => err);
                }),
            );
    }
    /**
     * Blocking call
     */
    async createZip(fileUpload: FileUpload): Promise<any> {
        this.logger.trace7({ message: `Creating zip file.`, data: FileUpload.toLoggableObject(fileUpload) });
        const processorMetadata = fileUpload.endpointData.processorMetadataList[0];
        return new Promise(async (resolve, reject) => {
            if (!processorMetadata.processDomain) {
                reject(new Error('File is not processed!')); return;
            }
            const url = ProcessorMetadataUtils.getZipEndpoint(processorMetadata);
            const formData: FormData = new FormData();

            // `result` is a ProcessResult by the time the tracker has parsed it,
            // but a storage-recovered FileUpload can still hold the raw JSON
            // string — hence the cast and the parse below.
            let result = fileUpload.result;
            if (isString(result as any)) {
                try { result = JSON.parse(fileUpload.result as any); } catch (e) { reject(e); return; }
            }




            const zipFile = result.zip;
            const outputFiles = castArray(result.output);

            formData.append('id', fileUpload.id);
            formData.append('wsId', this.wsService.getId());
            formData.append('wsDomain', this.wsService.getDomain());
            formData.append('zipFile', zipFile);
            // D13 / FRAME_PICKER_DESIGN.md §4.2: when the card carries the
            // schedule workflow id (B3), send it and let the edge enumerate the
            // owned workflow's `processResult.outputFiles` itself.
            // Review 7 P2-01: send ONLY that. Tomcat's `max-part-count=200`
            // counts multipart PARTS, so the old unconditional `outputFiles`
            // loop drew a 413 for any job past ~195 files (600 frames on QA).
            // Every edge reads `workflowUuid` since `54ae4608`, and since
            // `8b670be5` refuses a `zipFile` that is not the workflow's own --
            // so this ships with (or after) that edge build. A card with no
            // workflow id (recovered from before B3) keeps the old shape.
            if (fileUpload.workflowId) {
                formData.append('workflowUuid', fileUpload.workflowId);
            } else {
                (outputFiles || []).forEach(file => {
                    formData.append('outputFiles', file);
                });
            }

            await firstValueFrom(this.http.request(url, 'POST', formData, { headers: this.helperService.getCommonHttpHeaders() })
                .response$)
                .then(resp => {
                    this.logger.trace7({ message: `Creating zip file success.`, data: resp });
                    resolve(fileUpload);
                })
                .catch(err => {
                    this.logger.error({ message: `Error creating zip file.`, data: FileUpload.toLoggableObject(fileUpload), error: err });
                    reject(err);
                });
        });
    }

    /**
     * Sample-encode estimator probe — the ONE correct implementation.
     *
     * Replaces the previous probeProcessForInfo, which was dead code twice
     * over: it posted to /files/input/probe (a URL that never existed on
     * ironman — the real mapping is /files/source/probe) and appended raw
     * multipart file bytes, which the endpoint doesn't accept (it resolves
     * `originalInputFiles` references to files already uploaded to that
     * edge). Call POST-UPLOAD only, against the edge holding the file.
     */
    async probeSourceForInfo(params: SourceProbeParams): Promise<SourceProbeInfo> {
        const url = ApiConfig.suffixDomainWithHttpsEdgeEndpoint(params.processorMetadata.uploadDomain) + '/files/source/probe';
        const formData: FormData = new FormData();
        formData.append('originalInputFiles', JSON.stringify(params.originalInputFiles));
        formData.append('probeDuration', String(params.probeDuration ?? 1));
        formData.append('id', params.id);
        if (params.processType) {
            formData.append('processType', params.processType);
        }
        formData.append('fromType', castArray(params.fromType).toString());
        formData.append('toType', params.toType);
        formData.append('wsId', this.wsService.getId());
        formData.append('wsDomain', this.wsService.getDomain());
        formData.append('options', JSON.stringify(params.options ?? {}));
        formData.append('log', 'false');
        formData.append('estimateOnly', String(!!params.estimateOnly));

        this.logger.trace7({ message: 'Probing source for info.', data: { id: params.id, toType: params.toType, estimateOnly: !!params.estimateOnly } });
        try {
            const data = await firstValueFrom(this.http.request<SourceProbeInfo>(
                url, 'POST', formData,
                { headers: this.helperService.getCommonHttpHeaders(), timeout: 180000 },
            ).response$);
            this.logger.trace7({ message: 'Success probing source for info.', data });
            return (data as any)?.body ?? data;
        } catch (err) {
            this.logger.error({ message: 'Error probing source for info.', data: { id: params.id }, error: err });
            throw err;
        }
    }
}
